import {Button, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import {useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {User} from "crqlar-react-common/dist/models/identity/user.model";
import {useNavigate} from "react-router-dom";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import EntityActions from "crqlar-react-common/dist/components/EntityActions";
import ImagePicker from "crqlar-react-common/dist/components/ImagePicker";
import Property from "crqlar-react-common/dist/components/Property";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useDispatch} from "react-redux";
import { AuthActions } from "crqlar-react-common/dist/state/auth.reducer";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import {syncMixpanelAuth} from "../../utils/analytics/mixpanelAuth";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import {PageShell} from "../layout/PageShell";
import AccountSessionActions from "./AccountSessionActions";
import ChangeMyPasswordDialog from "./ChangeMyPasswordDialog";

const MyAccountDetails = () => {

    //region State

    useTrackScreenVisit('my account details')

    const {t} = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)

    const accountData = useEntity<User>({
        get: async () => {
            return await MyAccountService.getMyAccount()
        },
        update: MyAccountService.update,
        create: MyAccountService.update,
        showSaveSuccess: true,
        canDelete: i => false
    })

    const account = accountData.item
    const setAccount = accountData.setItem


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof User, value: any) => {
        setAccount({...account, [field]: value})
    }

    const logout = () => {
        localStorage.removeItem('token')
        syncMixpanelAuth(undefined)

        dispatch({
            type: AuthActions.SET_USER,
            payload: undefined
        })

        navigate('/identity/login')
    }

    //endregion Handlers

    //region UI

    const titleSection = (
        <>
            <Stack
                direction={'row'}
                sx={{
                    justifyContent: 'space-between',
                    // position: 'sticky',
                    // top: 0
                }}

            >
                <Typography variant={'h4'} textAlign={'start'}>
                    {account.firstName || '-'}
                </Typography>

                <EntityActions data={accountData}/>
            </Stack>

            <Stack mb={2} direction={'row'} justifyContent={'flex-start'}>
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                >
                    {t('Last change at', {time: account.updateDate?.toLocaleDateString() || '-'})}
                </Typography>
            </Stack>

            <ImagePicker
                image={account.avatar}
                setImage={(image) => updateField('avatar', image)}
                width={150}
                height={150}
                objectFit={'contain'}
                id={'my-account-image'}
            />
        </>
    )

    const basicInfoSection = (
        <>
            <Typography variant={'h6'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Property
                title={t('First name')}
            >
                <TextField
                    id={'firstName'}
                    size={'small'}
                    value={account.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    fullWidth
                    required
                />
            </Property>

            <Divider/>

            <Property
                title={t('Last name')}
            >
                <TextField
                    id={'lastName'}
                    size={'small'}
                    value={account.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    fullWidth
                    required
                />
            </Property>

            <Divider/>

            <Property
                title={t('Email')}
            >
                <TextField
                    id={'email'}
                    size={'small'}
                    value={account.email}
                    fullWidth
                    disabled
                    InputProps={{
                        endAdornment: <CopyAdornment value={account.email || ''} />,
                    }}
                />
            </Property>

        </>
    )

    const passwordSection = (
        <>
            <Typography variant={'h6'} textAlign={'start'}>
                {t('Password & security')}
            </Typography>

            <Property
                title={t('Password')}
                tooltip={t('The password you use to sign in to CRQLAR.')}
            >
                <Stack direction={'row'} justifyContent={'flex-end'} sx={{width: '100%'}}>
                    <Button
                        id={'btn-change-my-password'}
                        variant={'grayOutlined'}
                        size={'small'}
                        onClick={() => setPasswordDialogOpen(true)}
                    >
                        {t('Change password')}
                    </Button>
                </Stack>
            </Property>
        </>
    )

    //endregion UI

    const accountDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '800px',
                gap: 1,
                alignSelf: 'center'
            }}
        >
            {titleSection}
            {basicInfoSection}

            <Divider sx={{my: 4}}/>

            {passwordSection}

            <Divider sx={{my: 4}}/>

            <AccountSessionActions onLogout={logout}/>

            <ChangeMyPasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
            />
        </Stack>
    )

    return (
        <PageShell
            icon={<PersonOutlinedIcon fontSize="inherit"/>}
            title={t('My account')}
            documentTitle={t('My account')}
            tabs={[]}
        >
            <ScreenWrapper>
                {accountDataUI}
            </ScreenWrapper>
        </PageShell>
    );
}

export default MyAccountDetails;
